import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/ui/Sidebar';
import QuickActionsToolbar from '../../components/ui/QuickActionsToolbar';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import ProjectCard from './components/ProjectCard';
import CreateProjectModal from './components/CreateProjectModal';
import DeleteProjectModal from './components/DeleteProjectModal';
import ProjectStatsPanel from './components/ProjectStatsPanel';
import projectService from '../../services/projectService';

const ProjectLandingDashboard = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [deletingProject, setDeletingProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch projects from Supabase
  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    setLoading(true);
    setError(null);
    const { data, error } = await projectService?.getAllProjects();
    if (error) {
      setError(error);
      setProjects([]);
    } else {
      // Transform snake_case to camelCase
      const transformedData = data?.map(project => ({
        id: project?.id,
        name: project?.name,
        projectNo: project?.project_no,
        description: project?.description,
        status: project?.status,
        progress: project?.progress,
        owner: project?.owner,
        teamMembers: project?.team_members || [],
        lastModified: project?.updated_at,
        createdAt: project?.created_at,
        workflowCount: project?.workflow_count,
        imageUrl: project?.image_url,
        clientId: project?.client_id,
        client: project?.clients?.company_name || null,
        projectType: project?.project_type,
        projectCountry: project?.project_country
      })) || [];
      setProjects(transformedData);
      setFilteredProjects(transformedData);
    }
    setLoading(false);
  };

  // Filter and search logic
  useEffect(() => {
    let result = projects;

    // Filter by status
    if (filterStatus !== 'all') {
      result = result?.filter(p => p?.status === filterStatus);
    }

    // Search by name or description
    if (searchQuery) {
      result = result?.filter(p =>
        p?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        p?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase())
      );
    }

    setFilteredProjects(result);
  }, [projects, searchQuery, filterStatus]);

  const handleCreateProject = async (projectData) => {
    const { data, error } = await projectService?.createProject(projectData);
    if (error) {
      alert(`Error creating project: ${error}`);
    } else {
      await fetchProjects();
      setIsCreateModalOpen(false);
    }
  };

  const handleUpdateProject = async (projectData) => {
    const { data, error } = await projectService?.updateProject(editingProject?.id, projectData);
    if (error) {
      alert(`Error updating project: ${error}`);
    } else {
      setProjects(projects?.map(p => p?.id === data?.id ? {
        ...data,
        clientName: data?.clients?.company_name || null
      } : p));
      handleCloseModal();
    }
  };

  const handleEditProject = (project) => {
    setEditingProject({
      id: project?.id,
      name: project?.name,
      projectNo: project?.projectNo,
      description: project?.description,
      owner: project?.owner,
      teamMembers: project?.teamMembers || [],
      imageUrl: project?.imageUrl,
      clientId: project?.clientId,
      projectType: project?.projectType,
      projectCountry: project?.projectCountry
    });
    setIsCreateModalOpen(true);
  };

  const handleDeleteProject = (project) => {
    setDeletingProject(project);
  };

  const handleProjectClick = (project) => {
    navigate(`/project/${project?.id}/dashboard`);
  };

  const handleConfirmDelete = async (projectId) => {
    const { error } = await projectService?.deleteProject(projectId);
    if (error) {
      alert(`Error deleting project: ${error}`);
    } else {
      await fetchProjects();
      setDeletingProject(null);
    }
  };

  const handleCloseModal = () => {
    setIsCreateModalOpen(false);
    setEditingProject(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'var(--color-success)';
      case 'planning':
        return 'var(--color-warning)';
      case 'completed':
        return 'var(--color-primary)';
      case 'on-hold':
        return 'var(--color-muted-foreground)';
      default:
        return 'var(--color-muted-foreground)';
    }
  };

  return (
    <div className="flex h-screen bg-background">
      <Sidebar />
      
      <div className="flex-1 flex flex-col overflow-hidden ml-60">
        <QuickActionsToolbar />

        {/* Top Action Bar */}
        <div className="bg-card border-b border-border px-6 py-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex-1 max-w-md">
              <div className="relative">
                <Icon
                  name="Search"
                  size={18}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground"
                />
                <input
                  type="text"
                  placeholder="Search projects..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                  className="w-full h-10 pl-10 pr-4 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 bg-muted rounded-lg p-1">
                <button
                  onClick={() => setFilterStatus('all')}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                    filterStatus === 'all' ?'bg-white shadow-sm text-foreground' :'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  All
                </button>
                <button
                  onClick={() => setFilterStatus('active')}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                    filterStatus === 'active' ?'bg-white shadow-sm text-foreground' :'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Active
                </button>
                <button
                  onClick={() => setFilterStatus('planning')}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                    filterStatus === 'planning' ?'bg-white shadow-sm text-foreground' :'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Planning
                </button>
                <button
                  onClick={() => setFilterStatus('completed')}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                    filterStatus === 'completed'
                      ? 'bg-white shadow-sm text-foreground' :'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Completed
                </button>
              </div>

              <Button
                iconName="Plus"
                size="default"
                onClick={() => setIsCreateModalOpen(true)}
              >
                Create Project
              </Button>
            </div>
          </div>
        </div>

        {/* Main Project Grid */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
                <p className="text-muted-foreground">Loading projects...</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center max-w-md">
                <div className="w-20 h-20 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="AlertCircle" size={40} color="var(--color-destructive)" />
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-2">Error Loading Projects</h3>
                <p className="text-muted-foreground mb-6">{error}</p>
                <Button onClick={fetchProjects}>Try Again</Button>
              </div>
            </div>
          ) : filteredProjects?.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center max-w-md">
                <div className="w-20 h-20 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="FolderOpen" size={40} color="var(--color-muted-foreground)" />
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-2">
                  {searchQuery || filterStatus !== 'all' ? 'No projects found' : 'No projects yet'}
                </h3>
                <p className="text-muted-foreground mb-6">
                  {searchQuery || filterStatus !== 'all' ?'Try adjusting your search or filters' :'Get started by creating your first project'}
                </p>
                <Button iconName="Plus" onClick={() => setIsCreateModalOpen(true)}>
                  Create Project
                </Button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-[repeat(auto-fit,280px)] gap-4 justify-start">
              {filteredProjects?.map((project) => (
                <ProjectCard
                  key={project?.id}
                  project={project}
                  onClick={() => handleProjectClick(project)}
                  onEdit={() => handleEditProject(project)}
                  onDelete={() => handleDeleteProject(project)}
                  getStatusColor={getStatusColor}
                />
              ))}
            </div>
          )}
        </div>

        {/* Stats Panel */}
        <ProjectStatsPanel projects={projects} />
      </div>

      {/* Create/Edit Modal */}
      {isCreateModalOpen && (
        <CreateProjectModal
          onClose={handleCloseModal}
          onSubmit={editingProject ? handleUpdateProject : handleCreateProject}
          editingProject={editingProject}
        />
      )}

      {/* Delete Modal */}
      <DeleteProjectModal
        isOpen={!!deletingProject}
        onClose={() => setDeletingProject(null)}
        project={deletingProject}
        onConfirmDelete={handleConfirmDelete}
      />
    </div>
  );
};

export default ProjectLandingDashboard;